/**
 * ApprovalChainBuilder — define the ordered list of approvers for an
 * evaluation before submission. Pick users via ApproverPicker, drag to
 * reorder (desktop) or use up/down arrows (mobile), Save persists via
 * D1's POST /api/evaluations/:evalId/approvals/build.
 *
 * Sequence is the array index + 1 — the slice / service handles the
 * normalisation but we send explicit numbers anyway so the order is
 * unambiguous on the wire.
 *
 * Mounted by ApprovalWorkspace (D4 / MYD-203). For now the component is
 * self-contained — D4 wires it into the workspace shell.
 *
 * Linear: MYD-201 (D2).
 */

import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
	GripVertical,
	X,
	ChevronUp,
	ChevronDown,
	Save,
	Loader2,
	Users,
	AlertCircle,
} from 'lucide-react'

import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import useMediaQuery from 'hooks/useMediaQuery'

import { buildChain } from 'slices/approvalSlice'
import ApproverPicker, { type ApproverUser } from './ApproverPicker'

interface ApprovalChainBuilderProps {
	evalId: string
	/** Optional callback after Save succeeds (D4 will use this to flip view state). */
	onSaved?: () => void
}

interface ChainRow {
	user: ApproverUser
	// Local key for dnd; equal to user._id since duplicates are filtered.
	key: string
}

interface SortableRowProps {
	row: ChainRow
	index: number
	count: number
	disableDrag: boolean
	onMoveUp: () => void
	onMoveDown: () => void
	onRemove: () => void
}

const SortableRow = ({
	row,
	index,
	count,
	disableDrag,
	onMoveUp,
	onMoveDown,
	onRemove,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id: row.key, disabled: disableDrag })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(
				'flex items-center gap-2 px-2 py-2 rounded-md border bg-white',
				isDragging
					? 'border-[#0080fe] shadow-md'
					: 'border-gray-200 hover:border-gray-300',
			)}
		>
			{/* Drag handle (desktop) — listeners only attach when drag is enabled */}
			{!disableDrag ? (
				<button
					type="button"
					{...attributes}
					{...listeners}
					aria-label={`Drag approver ${index + 1}`}
					className="p-1 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
				>
					<GripVertical className="w-4 h-4" />
				</button>
			) : (
				<div className="flex flex-col gap-0.5">
					<button
						type="button"
						onClick={onMoveUp}
						disabled={index === 0}
						aria-label={`Move ${row.user.name ?? 'approver'} up`}
						className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
					>
						<ChevronUp className="w-3.5 h-3.5" />
					</button>
					<button
						type="button"
						onClick={onMoveDown}
						disabled={index === count - 1}
						aria-label={`Move ${row.user.name ?? 'approver'} down`}
						className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
					>
						<ChevronDown className="w-3.5 h-3.5" />
					</button>
				</div>
			)}

			<div
				className="w-6 h-6 rounded-full bg-[#0080fe]/10 text-[#0080fe] flex items-center justify-center text-xs font-semibold shrink-0"
				aria-hidden
			>
				{index + 1}
			</div>

			<div className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 text-[10px] font-semibold uppercase">
				{(row.user.name || row.user.email || '?').slice(0, 1)}
			</div>

			<div className="flex-1 min-w-0">
				<div className="font-medium text-sm text-gray-900 truncate">
					{row.user.name || row.user.email || 'Unknown'}
				</div>
				{(row.user.email || row.user.title) && (
					<div className="text-xs text-gray-500 truncate">
						{row.user.title ? `${row.user.title} · ` : ''}
						{row.user.email}
					</div>
				)}
			</div>

			<button
				type="button"
				onClick={onRemove}
				aria-label={`Remove ${row.user.name ?? 'approver'} from chain`}
				className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-50 rounded shrink-0"
			>
				<X className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

const ApprovalChainBuilder = ({ evalId, onSaved }: ApprovalChainBuilderProps) => {
	const dispatch = useDispatch()
	const isDesktop = useMediaQuery('(min-width: 768px)')

	// Existing approvals (from slice) — populates the initial chain on mount or
	// when the user navigates between evaluations.
	const existing: any[] = useSelector(
		(state: any) => state.approvals?.byEvaluation?.[evalId] || [],
	)

	const [chain, setChain] = useState<ChainRow[]>([])
	const [isSaving, setIsSaving] = useState(false)
	const [saveError, setSaveError] = useState<string | null>(null)

	// Hydrate from `existing` once per evalId. Runs again if the evaluation
	// changes (so the form stays in sync when the parent swaps eval context).
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const hydrated: ChainRow[] = existing
			.slice()
			.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
			.map((a) => ({
				user: {
					_id:
						typeof a.approver === 'object' && a.approver
							? a.approver._id
							: String(a.approver),
					name: a.approver?.name,
					email: a.approver?.email,
					title: a.approver?.title,
					company: a.approver?.company,
					image: a.approver?.image,
				},
				key:
					typeof a.approver === 'object' && a.approver
						? a.approver._id
						: String(a.approver),
			}))
		setChain(hydrated)
	}, [evalId, existing.length])

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) return
		setChain((prev) => {
			const oldIndex = prev.findIndex((r) => r.key === active.id)
			const newIndex = prev.findIndex((r) => r.key === over.id)
			if (oldIndex === -1 || newIndex === -1) return prev
			return arrayMove(prev, oldIndex, newIndex)
		})
	}

	const moveBy = (index: number, delta: number) => {
		setChain((prev) => {
			const next = [...prev]
			const target = index + delta
			if (target < 0 || target >= next.length) return prev
			const [item] = next.splice(index, 1)
			next.splice(target, 0, item)
			return next
		})
	}

	const handleAdd = (user: ApproverUser) => {
		setChain((prev) => {
			if (prev.some((r) => r.user._id === user._id)) return prev
			return [...prev, { user, key: user._id }]
		})
	}

	const handleRemove = (key: string) => {
		setChain((prev) => prev.filter((r) => r.key !== key))
	}

	const excludeIds = useMemo(() => chain.map((r) => r.user._id), [chain])

	const dirty = useMemo(() => {
		// Compare by ordered ids. Hydration uses the same key so this is stable.
		const existingIds = existing
			.slice()
			.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
			.map((a) =>
				typeof a.approver === 'object' && a.approver
					? a.approver._id
					: String(a.approver),
			)
		const currentIds = chain.map((r) => r.user._id)
		if (existingIds.length !== currentIds.length) return true
		return existingIds.some((id, i) => id !== currentIds[i])
	}, [existing, chain])

	const handleSave = async () => {
		if (chain.length === 0) return
		setIsSaving(true)
		setSaveError(null)
		// @ts-ignore — thunk dispatch
		const result: any = await dispatch(
			buildChain({
				evalId,
				approvers: chain.map((r, i) => ({
					userId: r.user._id,
					sequence: i + 1,
				})),
			}),
		)
		setIsSaving(false)
		if (result?.error) {
			setSaveError(result.payload || 'Failed to save chain')
			return
		}
		onSaved?.()
	}

	return (
		<Card>
			<CardHeader className="pb-3 flex-row items-center justify-between gap-3">
				<div>
					<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
						<Users className="w-4 h-4" />
						Approval chain ({chain.length})
					</h4>
					<p className="text-xs text-gray-500 mt-0.5">
						{isDesktop
							? 'Drag to reorder. Sequence numbers update live.'
							: 'Use the up/down arrows to reorder.'}
					</p>
				</div>
				<ApproverPicker excludeIds={excludeIds} onPick={handleAdd} />
			</CardHeader>
			<CardContent>
				{chain.length === 0 ? (
					<div className="text-center py-8 border border-dashed border-gray-200 rounded-md">
						<Users className="mx-auto h-8 w-8 text-gray-300 mb-2" />
						<p className="text-sm text-gray-500 max-w-sm mx-auto">
							No approvers yet. Click <strong>Add approver</strong> above to start
							building the chain.
						</p>
					</div>
				) : (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={chain.map((r) => r.key)}
							strategy={verticalListSortingStrategy}
						>
							<div className="space-y-1.5">
								{chain.map((row, idx) => (
									<SortableRow
										key={row.key}
										row={row}
										index={idx}
										count={chain.length}
										disableDrag={!isDesktop}
										onMoveUp={() => moveBy(idx, -1)}
										onMoveDown={() => moveBy(idx, +1)}
										onRemove={() => handleRemove(row.key)}
									/>
								))}
							</div>
						</SortableContext>
					</DndContext>
				)}

				{saveError && (
					<div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
						<AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
						<span>{saveError}</span>
					</div>
				)}

				<div className="mt-4 flex items-center justify-end gap-2">
					{dirty && chain.length > 0 && (
						<span className="text-xs text-gray-500 italic">
							Unsaved changes
						</span>
					)}
					<Button
						variant="primary"
						disabled={chain.length === 0 || isSaving || !dirty}
						onClick={handleSave}
						className="gap-1.5"
					>
						{isSaving ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Saving…
							</>
						) : (
							<>
								<Save className="w-4 h-4" />
								Save chain
							</>
						)}
					</Button>
				</div>
			</CardContent>
		</Card>
	)
}

export default ApprovalChainBuilder
